import api from '@/shared/api';
import { buildTruthySearchParams, handleApiError, requestExcelBlobExport } from '@/shared/lib';
import type {
  FunctionalActivityPermissionsListReport,
  PermissionMatrixPositionType,
  PermissionMatrixResponse,
  PositionPermissionsListReport,
  RegistrationPermissionsReport,
  UpdatePositionPermissionsRequest,
  UpdatePositionPermissionsResponse,
} from '../model/types';

export interface ExportPermissionsExcelParams {
  categoryId?: number;
  term?: string;
  positionType?: PermissionMatrixPositionType;
  sort?: string;
  order?: string;
}

const PERMISSIONS_EXPORT_QUERY_KEYS = ['categoryId', 'term', 'positionType', 'sort', 'order'] as const;

class PermissionApi {
  async getPermissionsMatrix(
    positionType: PermissionMatrixPositionType = 'POSITION'
  ): Promise<PermissionMatrixResponse> {
    try {
      const response = await api.get<PermissionMatrixResponse>('/permissions/matrix', {
        params: { positionType },
      });
      return response.data;
    } catch (error: unknown) {
      throw handleApiError(error, 'Erro ao buscar matriz de permissões');
    }
  }

  async updatePositionPermissions(
    data: UpdatePositionPermissionsRequest,
    positionType: PermissionMatrixPositionType = 'POSITION'
  ): Promise<UpdatePositionPermissionsResponse> {
    try {
      if (positionType === 'FUNCTIONAL_ACTIVITY') {
        await api.patch(`/functional-activities/${data.positionId}/permissions`, {
          categoryId: data.categoryId,
          permissionIds: data.permissionIds,
        });
        return {
          success: true,
          message: 'Permissões atualizadas com sucesso',
        };
      }

      const response = await api.patch<UpdatePositionPermissionsResponse>(
        `/positions/${data.positionId}/permissions`,
        {
          categoryId: data.categoryId,
          permissionIds: data.permissionIds,
        }
      );
      return response.data;
    } catch (error: unknown) {
      throw handleApiError(error, 'Erro ao atualizar permissões');
    }
  }

  async exportPermissionsExcel(
    params: ExportPermissionsExcelParams
  ): Promise<{ blob: Blob; exportedCount: number; filename: string }> {
    const queryParams = buildTruthySearchParams(params, PERMISSIONS_EXPORT_QUERY_KEYS);
    return requestExcelBlobExport(
      () =>
        api.get(`/permissions/export/excel?${queryParams.toString()}`, {
          responseType: 'blob',
        }),
      'permissoes.xlsx',
      'Tente novamente.'
    );
  }

  async getRegistrationPermissions(
    registrationNumber: string
  ): Promise<RegistrationPermissionsReport> {
    try {
      const response = await api.get<{ success: boolean; data: RegistrationPermissionsReport }>(
        `/permissions/registration/${registrationNumber}`
      );
      return response.data.data;
    } catch (error: unknown) {
      throw handleApiError(error, 'Erro ao buscar permissões da matrícula');
    }
  }

  async getBulkPositionPermissions(positionIds: number[]): Promise<PositionPermissionsListReport> {
    try {
      const response = await api.post<{ success: boolean; data: PositionPermissionsListReport }>(
        '/permissions/positions/list',
        { positionIds }
      );
      return response.data.data;
    } catch (error: unknown) {
      throw handleApiError(error, 'Erro ao buscar permissões dos cargos');
    }
  }

  async getBulkFunctionalActivityPermissions(
    functionalActivityIds: number[]
  ): Promise<FunctionalActivityPermissionsListReport> {
    try {
      const response = await api.post<{
        success: boolean;
        data: FunctionalActivityPermissionsListReport;
      }>('/permissions/functional-activities/list', {
        functionalActivityIds,
      });
      return response.data.data;
    } catch (error: unknown) {
      throw handleApiError(error, 'Erro ao buscar permissões das atividades funcionais');
    }
  }
}

export const permissionApi = new PermissionApi();
